import React, { useEffect, useMemo, useState } from "react";
import { db, functions } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import "./UsersPanel.scss";

/* -------------------- UserDetails (inline page) -------------------- */
function UserDetails({ user, onBack, onUserUpdated }) {
  const [fn, setFn] = useState("");
  const [ln, setLn] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [rate, setRate] = useState(0);
  const [net30, setNet30] = useState(false);
  const [stripeFee, setStripeFee] = useState(Boolean(user.stripeFee || false));
  const [role, setRole] = useState(Number(user.role || 3));
  const [org, setOrg] = useState("");
  const [clientId, setClientId] = useState("");
  const [avatar, setAvatar] = useState(
    user.profileImageURL || user.profileImageUrl || user.photoURL || ""
  );
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [forcingVerification, setForcingVerification] = useState(false);
  const [forceStatus, setForceStatus] = useState("");

  useEffect(() => {
    setFn(user.firstName || "");
    setLn(user.lastName || "");
    setEmail(user.email || "");
    setPhone(user.phone || user.phoneNumber || "");
    setRate(Number(user.rate || 0));
    setNet30(Boolean(user.net30Approved || user.net30 || false));
    setStripeFee(Boolean(user.stripeFee || false));
    setRole(Number(user.role || 3));
    setOrg(user?.freshbooksClientId?.organization || user.organization || "");
    setClientId(
      String(user?.freshbooksClientId?.id || user?.freshbooksClientId || "")
    );
    setAvatar(
      user.profileImageURL || user.profileImageUrl || user.photoURL || ""
    );
    setIsEditing(false);
  }, [user]);

  useEffect(() => {
    let isActive = true;
    async function loadOrders() {
      setOrdersLoading(true);
      try {
        // assumes orders collection stores a userId field matching users/{id}
        const q = query(
          collection(db, "orders"),
          where("customerId", "==", user.id),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (isActive) setOrders(rows);
      } catch (e) {
        console.error("loadOrders failed", e);
        if (isActive) setOrders([]);
      } finally {
        if (isActive) setOrdersLoading(false);
      }
    }
    if (user?.id) loadOrders();
    return () => {
      isActive = false;
    };
  }, [user?.id]);

  const saveBasics = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.id), {
        firstName: fn,
        lastName: ln,
        email,
        phone,
        phoneNumber: phone,
      });
      onUserUpdated?.({
        ...user,
        firstName: fn,
        lastName: ln,
        email,
        phone,
        phoneNumber: phone,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveRate = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.id), { rate: Number(rate) });
      onUserUpdated?.({ ...user, rate: Number(rate) });
    } finally {
      setSaving(false);
    }
  };

  const toggleNet30 = async () => {
    const next = !net30;
    setNet30(next);
    try {
      await updateDoc(doc(db, "users", user.id), { net30Approved: next });
      onUserUpdated?.({ ...user, net30Approved: next });
    } catch (e) {
      console.error(e);
      setNet30(!next);
    }
  };

  const toggleStripeFee = async () => {
    const next = !stripeFee;
    setStripeFee(next);
    try {
      await updateDoc(doc(db, "users", user.id), { stripeFee: next });
      onUserUpdated?.({ ...user, stripeFee: next });
    } catch (e) {
      console.error(e);
      setStripeFee(!next);
    }
  };

  const saveFreshbooks = async () => {
    setSaving(true);
    try {
      const payload = clientId
        ? { freshbooksClientId: { id: clientId, organization: org } }
        : { freshbooksClientId: null, organization: org || null };
      await updateDoc(doc(db, "users", user.id), payload);
      onUserUpdated?.({ ...user, ...payload });
    } finally {
      setSaving(false);
    }
  };

  const removeFreshbooks = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.id), { freshbooksClientId: null });
      onUserUpdated?.({ ...user, freshbooksClientId: null });
      setClientId("");
    } finally {
      setSaving(false);
    }
  };

  const ROLE_LABEL_LOCAL = { 1: "Staff", 2: "Business", 3: "Personal" };

  const saveRole = async () => {
    setSaving(true);
    try {
      const num = Number(role) || 3;
      await updateDoc(doc(db, "users", user.id), { role: num });
      onUserUpdated?.({ ...user, role: num });
    } finally {
      setSaving(false);
    }
  };

  const hasEmailVerified =
    user.emailVerified !== undefined && user.emailVerified !== null;
  const emailVerified = hasEmailVerified ? user.emailVerified === true : null;
  const verificationLabel =
    emailVerified === true
      ? "Verified"
      : emailVerified === false
        ? "Not verified"
        : "Unknown";

  const uploadAvatar = async (file) => {
    if (!file) return;
    try {
      setSaving(true);
      const storage = getStorage();
      const r = ref(storage, `profilePictures/${user.id}`);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      await updateDoc(doc(db, "users", user.id), { profileImageURL: url });
      setAvatar(url);
      onUserUpdated?.({ ...user, profileImageURL: url });
    } catch (e) {
      console.error("upload failed", e);
      alert("Failed to upload image");
    } finally {
      setSaving(false);
    }
  };

  const triggerForceVerification = async () => {
    if (!user?.id) return;
    setForceStatus("");
    setForcingVerification(true);
    try {
      const callable = httpsCallable(functions, "forceEmailVerification");
      const result = await callable({ uid: user.id });
      if (result?.data?.ok) {
        setForceStatus("Verification email sent and user marked as pending.");
      } else {
        setForceStatus("Unable to trigger verification. Please try again.");
      }
      onUserUpdated?.({
        ...user,
        requireEmailReverify: true,
        emailVerified: false,
      });
    } catch (err) {
      console.error("force verification failed", err);
      setForceStatus(
        err?.message || "Failed to trigger verification. Try again."
      );
    } finally {
      setForcingVerification(false);
    }
  };

  return (
    <div className="ud-page">
      <div className="ud-head">
        <button className="op-link" onClick={onBack}>
          ← Back to Customers
        </button>
        <h2>Customer Details</h2>
        <div className="ud-head__right">
          {isEditing ? (
            <button
              className="cu-btn"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
          ) : (
            <button
              className="cu-btn cu-btn--primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          )}
          <button
            className="cu-btn"
            onClick={triggerForceVerification}
            disabled={forcingVerification}
          >
            {forcingVerification ? "Sending..." : "Force Email Verification"}
          </button>
        </div>
      </div>
      {forceStatus ? (
        <div
          className="ud-alert"
          style={{
            marginBottom: 16,
            padding: "8px 12px",
            borderRadius: 6,
            background: "rgba(148, 201, 64, 0.1)",
            color: "#3c4b1a",
            fontSize: 14,
          }}
        >
          {forceStatus}
        </div>
      ) : null}

      <div className="ud-grid">
        {/* Details Header Grid */}
        <section className="ud-card">
          <div className="ud-section ud-header">
            <div className="ud-headerGrid">
              {/* LEFT: Avatar */}
              <div className="ud-headerGrid__left">
                <div className="ud-avatar">
                  {avatar ? (
                    <img src={avatar} alt="avatar" />
                  ) : (
                    <div className="ud-initials">
                      {(fn[0] || "?") + (ln[0] || "")}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <label className="ud-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => uploadAvatar(e.target.files?.[0])}
                    />
                    Upload new photo
                  </label>
                )}
              </div>

              {/* RIGHT: First/Last on top row, Email then Phone */}
              <div className="ud-headerGrid__right">
                {isEditing ? (
                  <div className="ud-form ud-form--header">
                    <label>
                      <span>First name</span>
                      <input
                        value={fn}
                        onChange={(e) => setFn(e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Last name</span>
                      <input
                        value={ln}
                        onChange={(e) => setLn(e.target.value)}
                      />
                    </label>
                    <label className="ud-colspan">
                      <span>Email</span>
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </label>
                    <label className="ud-colspan">
                      <span>Phone</span>
                      <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </label>
                    <div className="ud-actions">
                      <button
                        className="cu-btn cu-btn--primary"
                        disabled={saving}
                        onClick={saveBasics}
                      >
                        {saving ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="ud-view ud-view--header">
                    {/* Top-left: Name */}
                    <div className="ud-field">
                      <span className="ud-field__label">Name</span>
                      <span className="ud-field__value">
                        {`${fn} ${ln}`.trim() || "—"}
                      </span>
                    </div>

                    {/* Top-right: Organization (only if present). If not, render a spacer so grid stays aligned */}
                    {org ? (
                      <div className="ud-field">
                        <span className="ud-field__label">Organization</span>
                        <span className="ud-field__value">{org}</span>
                      </div>
                    ) : (
                      <div className="ud-spacer" aria-hidden="true" />
                    )}

                    {/* Bottom-left: Email */}
                    <div className="ud-field">
                      <span className="ud-field__label">Email</span>
                      <span className="ud-field__value">{email || "—"}</span>
                    </div>

                    {/* Bottom-right: Phone */}
                    <div className="ud-field">
                      <span className="ud-field__label">Phone</span>
                      <span className="ud-field__value">{phone || "—"}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ud-section">
            <div className="ud-title">Settings</div>
            {isEditing ? (
              <div className="ud-form">
                <label className="ud-colspan">
                  <span>Rate (USD)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </label>
                <div className="ud-actions">
                  <button
                    className="cu-btn"
                    disabled={saving}
                    onClick={saveRate}
                  >
                    {saving ? "Saving…" : "Update rate"}
                  </button>
                </div>
                <label className="ud-colspan">
                  <span>Net30 Access</span>
                  <span
                    className={`ud-badge ${net30 ? "ud-badge--ok" : "ud-badge--off"}`}
                  >
                    {net30 ? "Approved" : "Not approved"}
                  </span>
                </label>

                <div className="ud-actions">
                  <button className="cu-btn" onClick={toggleNet30}>
                    {net30 ? "Revoke" : "Approve"}
                  </button>
                </div>

                <label className="ud-colspan">
                  <span>Stripe Fee</span>
                  <span
                    className={`ud-badge ${stripeFee ? "ud-badge--ok" : "ud-badge--off"}`}
                  >
                    {stripeFee ? "Enabled" : "Disabled"}
                  </span>
                </label>

                <div className="ud-actions">
                  <button className="cu-btn" onClick={toggleStripeFee}>
                    {stripeFee ? "Disable Stripe Fee" : "Enable Stripe Fee"}
                  </button>
                </div>

                <label className="ud-colspan">
                  <span>Role</span>
                  <select
                    value={role}
                    onChange={(e) => setRole(Number(e.target.value))}
                  >
                    <option value={1}>1 – Staff</option>
                    <option value={2}>2 – Business</option>
                    <option value={3}>3 – Personal</option>
                  </select>
                </label>
                <div className="ud-actions">
                  <button
                    className="cu-btn"
                    disabled={saving}
                    onClick={saveRole}
                  >
                    {saving ? "Saving…" : "Update role"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="ud-view_settings">
                <div className="ud-field ud-colspan">
                  <span className="ud-field__label">Rate (USD)</span>
                  <span className="ud-field__value">
                    {Number(rate || 0).toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                    })}
                  </span>
                </div>
                <div className="ud-field ud-colspan">
                  <span className="ud-field__label">Net30 Access</span>
                  <span
                    className={`ud-badge ${net30 ? "ud-badge--ok" : "ud-badge--off"}`}
                  >
                    {net30 ? "Approved" : "Not approved"}
                  </span>
                </div>
                <div className="ud-field ud-colspan">
                  <span className="ud-field__label">Stripe Fee</span>
                  <span
                    className={`ud-badge ${stripeFee ? "ud-badge--ok" : "ud-badge--off"}`}
                  >
                    {stripeFee ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <div className="ud-field ud-colspan">
                  <span className="ud-field__label">Email Verification</span>
                  <span
                    className={`ud-badge ${emailVerified === true ? "ud-badge--ok" : "ud-badge--off"}`}
                  >
                    {verificationLabel}
                  </span>
                </div>
                <div className="ud-field ud-colspan">
                  <span className="ud-field__label">Role</span>
                  <span className="ud-field__value">
                    {ROLE_LABEL_LOCAL[Number(role)] || "Personal"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Right column */}
        <aside className="ud-card">
          <div className="ud-section">
            <div className="ud-title">FreshBooks Integration</div>
            {isEditing ? (
              <div className="ud-form">
                <label>
                  <span>Organization</span>
                  <input value={org} onChange={(e) => setOrg(e.target.value)} />
                </label>
                <label>
                  <span>Client ID</span>
                  <input
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                  />
                </label>
                <div className="ud-actions">
                  <button
                    className="cu-btn"
                    disabled={saving}
                    onClick={saveFreshbooks}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    className="cu-btn"
                    disabled={saving}
                    onClick={removeFreshbooks}
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <div className="ud-view">
                <div className="ud-field">
                  <span className="ud-field__label">Organization</span>
                  <span className="ud-field__value">{org || "—"}</span>
                </div>
                <div className="ud-field">
                  <span className="ud-field__label">Client ID</span>
                  <span className="ud-field__value">{clientId || "—"}</span>
                </div>
              </div>
            )}
          </div>

          <div className="ud-section">
            <div className="ud-title">Meta</div>
            <div className="ud-meta">
              <span>{user.id}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function AddCustomerForm({ onCancel, onCreated }) {
  const [fn, setFn] = useState("");
  const [ln, setLn] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("United States");
  const [company, setCompany] = useState("");
  const [address, setAddress] = useState("");
  const [apt, setApt] = useState("");
  const [city, setCity] = useState("");
  const [stateProv, setStateProv] = useState("");
  const [zip, setZip] = useState("");
  const [note, setNote] = useState("");
  const [collectTax, setCollectTax] = useState(true);
  const [saving, setSaving] = useState(false);

  const canSave = fn.trim() || ln.trim() || email.trim();

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        firstName: fn.trim(),
        lastName: ln.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        emailVerified: false,
        country,
        organization: company.trim() || null,
        address: address.trim() || null,
        apartment: apt.trim() || null,
        city: city.trim() || null,
        state: stateProv.trim() || null,
        zip: zip.trim() || null,
        notes: note.trim() || null,
        role: 3, // Personal by default
        createdAt: Date.now(),
      };
      const ref = await addDoc(collection(db, "users"), payload);
      const created = { id: ref.id, ...payload };
      onCreated?.(created);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ud-page">
      <div className="ud-head">
        <button className="op-link" onClick={onCancel}>
          ← Back to Customers
        </button>
        <h2>New Customers</h2>
        <div className="ud-head__right">
          <button className="cu-btn" onClick={onCancel} disabled={saving}>
            Discard
          </button>
          <button
            className="cu-btn cu-btn--primary"
            onClick={handleSave}
            disabled={saving || !canSave}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="ud-grid">
        {/* LEFT COLUMN: Customers Overview, Tax, Notes */}
        <section className="ud-card">
          <div className="ud-section">
            <div className="ud-title">Customers Overview</div>
            <div className="ud-form">
              <label>
                <span>First name</span>
                <input value={fn} onChange={(e) => setFn(e.target.value)} />
              </label>
              <label>
                <span>Last name</span>
                <input value={ln} onChange={(e) => setLn(e.target.value)} />
              </label>
              <label className="ud-colspan">
                <span>Email</span>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="ud-colspan">
                <span>Phone number</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
            </div>
          </div>

          <div className="ud-section">
            <div className="ud-title">Tax exemptions</div>
            <div className="ud-form">
              <label className="ud-colspan">
                <span>Collect tax</span>
                <div>
                  <input
                    type="checkbox"
                    checked={collectTax}
                    onChange={(e) => setCollectTax(e.target.checked)}
                  />
                  <span style={{ marginLeft: 8 }}>
                    {collectTax ? "Collect tax" : "Don’t collect tax"}
                  </span>
                </div>
              </label>
            </div>
          </div>

          <div className="ud-section">
            <div className="ud-title">Notes</div>
            <div className="ud-form">
              <label className="ud-colspan">
                <span>Note</span>
                <textarea
                  rows={4}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </div>
          </div>
        </section>

        {/* RIGHT COLUMN: Address */}
        <aside className="ud-card">
          <div className="ud-section">
            <div className="ud-title">Address</div>
            <div className="ud-form">
              <label className="ud-colspan">
                <span>Country/region</span>
                <input
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </label>
              <label>
                <span>First name</span>
                <input value={fn} onChange={(e) => setFn(e.target.value)} />
              </label>
              <label>
                <span>Last name</span>
                <input value={ln} onChange={(e) => setLn(e.target.value)} />
              </label>
              <label className="ud-colspan">
                <span>Company</span>
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </label>
              <label className="ud-colspan">
                <span>Address</span>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </label>
              <label className="ud-colspan">
                <span>Apartment, suite, etc.</span>
                <input value={apt} onChange={(e) => setApt(e.target.value)} />
              </label>
              <label>
                <span>City</span>
                <input value={city} onChange={(e) => setCity(e.target.value)} />
              </label>
              <label>
                <span>State</span>
                <input
                  value={stateProv}
                  onChange={(e) => setStateProv(e.target.value)}
                />
              </label>
              <label className="ud-colspan">
                <span>ZIP code</span>
                <input value={zip} onChange={(e) => setZip(e.target.value)} />
              </label>
              <label className="ud-colspan">
                <span>Phone number</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* --------------------------- UsersPanel --------------------------- */
export default function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("All"); // All | Business | Individual
  const [activeUser, setActiveUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [orderCounts, setOrderCounts] = useState({}); // { [userId]: number }
  const [orderSums, setOrderSums] = useState({}); // { [userId]: number in USD }
  const [lastOrders, setLastOrders] = useState({}); // { [userId]: ms timestamp }
  useEffect(() => {
    const fetchOrderStats = async () => {
      try {
        const snap = await getDocs(collection(db, "orders"));
        const counts = {};
        const sums = {};
        const lasts = {};
        const toNumber = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : 0;
        };
        const extractAmount = (data) => {
          // Try a few common fields; fall back to 0
          if (typeof data.totalCents === "number") return data.totalCents / 100;
          if (typeof data.amountCents === "number")
            return data.amountCents / 100;
          if (typeof data.grandTotalCents === "number")
            return data.grandTotalCents / 100;
          if (data.total != null) return toNumber(data.total);
          if (data.grandTotal != null) return toNumber(data.grandTotal);
          if (data.totalPrice != null) return toNumber(data.totalPrice);
          if (data.amount != null) return toNumber(data.amount);
          if (data.amountPaid != null) return toNumber(data.amountPaid);
          if (data.subtotal != null && data.tax != null)
            return toNumber(data.subtotal) + toNumber(data.tax);
          return 0;
        };
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          const cid = data.customerId || data.userId; // support legacy field name
          if (!cid) return;
          counts[cid] = (counts[cid] || 0) + 1;
          const amt = extractAmount(data);
          sums[cid] = (sums[cid] || 0) + amt;
          const ts = data.createdAt ? Number(data.createdAt) : null;
          if (ts) {
            lasts[cid] = Math.max(lasts[cid] || 0, ts);
          }
        });
        setOrderCounts(counts);
        setOrderSums(sums);
        setLastOrders(lasts);
      } catch (e) {
        console.error("Error fetching order stats:", e);
        setOrderCounts({});
        setOrderSums({});
        setLastOrders({});
      }
    };
    fetchOrderStats();
  }, []);

  // Role mapping from numeric codes on the user document
  // 1 = Staff, 2 = Business, 3 = Personal
  const ROLE_LABEL = { 1: "Staff", 2: "Business", 3: "Personal" };

  function getType(u) {
    const code = Number(u?.role);
    if (ROLE_LABEL[code]) return ROLE_LABEL[code];
    // fallback: if role is missing or invalid, default to Personal
    return "Personal";
  }

  const TypeBadge = ({ user }) => {
    const t = getType(user);
    return <span className={`cu-badge cu-badge--${t.toLowerCase()}`}>{t}</span>;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snap = await getDocs(collection(db, "users"));
        const usersData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const last = (u) =>
          (
            u.lastName ||
            (u.name || "").split(" ").slice(-1)[0] ||
            ""
          ).toLowerCase();
        const first = (u) =>
          (u.firstName || (u.name || "").split(" ")[0] || "").toLowerCase();
        usersData.sort(
          (a, b) =>
            last(a).localeCompare(last(b)) || first(a).localeCompare(first(b))
        );
        setUsers(usersData);
      } catch (e) {
        console.error("Error fetching users:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const byFilter = (u) => {
      if (filter === "Business") return getType(u) === "Business";
      if (filter === "Individual") return getType(u) === "Personal"; // "Individual" filter maps to Personal
      if (filter === "Staff") return getType(u) === "Staff"; // "Individual" filter maps to Personal
      return true; // All
    };
    return users.filter(byFilter).filter((u) => {
      if (!term) return true;
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      const org = (
        u?.freshbooksClientId?.organization ||
        u.organization ||
        u.companyName ||
        ""
      ).toLowerCase();
      const email = (u.email || "").toLowerCase();
      const location =
        `${u.city || ""} ${u.state || u.province || ""}`.toLowerCase();
      return (
        name.includes(term) ||
        org.includes(term) ||
        email.includes(term) ||
        location.includes(term)
      );
    });
  }, [users, searchTerm, filter]);
  const ITEMS_PER_PAGE = 20; // match Orders pagination size
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  const toDateSafe = (ts) => {
    if (!ts) return null;
    // Native Date instance
    if (ts instanceof Date) return ts;
    // Firestore Timestamp
    if (typeof ts?.toDate === "function") {
      try {
        return ts.toDate();
      } catch (_) {
        /* ignore */
      }
    }
    // Firestore-like object {seconds, nanoseconds}
    if (
      typeof ts === "object" &&
      ts !== null &&
      typeof ts.seconds === "number"
    ) {
      return new Date(ts.seconds * 1000);
    }
    // Numeric epoch (allow seconds or ms)
    if (typeof ts === "number") {
      const ms = ts < 1e12 ? ts * 1000 : ts; // treat small numbers as seconds
      return new Date(ms);
    }
    // ISO string or other date-compatible string
    if (typeof ts === "string") return new Date(ts);
    return null;
  };
  const fmtDateDMY = (ts) => {
    const d = toDateSafe(ts);
    if (!(d instanceof Date)) return "—";
    const time = d.getTime();
    if (Number.isNaN(time)) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const fmtMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "$0.00";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  };

  const initials = (u) => {
    const f = (u.firstName || "").trim();
    const l = (u.lastName || "").trim();
    return (f[0] || "?") + (l[0] || "");
  };

  if (activeUser) {
    return (
      <UserDetails
        user={activeUser}
        onBack={() => setActiveUser(null)}
        onUserUpdated={(nu) => {
          setActiveUser(nu);
          setUsers((prev) => prev.map((x) => (x.id === nu.id ? nu : x)));
        }}
      />
    );
  }

  if (isAdding) {
    return (
      <AddCustomerForm
        onCancel={() => setIsAdding(false)}
        onCreated={(created) => {
          setUsers((prev) => [created, ...prev]);
          setIsAdding(false);
          setActiveUser(created); // jump into details after creation
        }}
      />
    );
  }

  return (
    <div className="cu-page">
      <div className="cu-shell">
        {/* Header */}
        <header className="cu-header">
          <h1>Customers</h1>
        </header>

        {/* Controls */}
        <div className="cu-controls">
          <div className="cu-search">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                fill="currentColor"
                d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 5 1.5-1.5-5-5zM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
              />
            </svg>
            <input
              placeholder="Search customer"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="cu-filters">
            {["All", "Business", "Individual", "Staff"].map((t) => (
              <button
                key={t}
                className={`cu-chip ${filter === t ? "is-active" : ""}`}
                onClick={() => {
                  setFilter(t);
                  setCurrentPage(1);
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="op-table">
          <div className="cu-head">
            <div className="cu-cell cu-cell--check">
              <input type="checkbox" aria-label="select all" />
            </div>
            <div className="op-cell cu-cell--customer">Customer</div>
            <div className="op-cell cu-cell--orders">Orders</div>
            <div className="op-cell cu-cell--last">Last Order</div>
            <div className="op-cell cu-cell--type">Account Type</div>
            <div className="op-cell cu-cell--spent">Spent</div>
            <div className="op-cell cu-cell--since">Created</div>{" "}
          </div>

          {loading ? (
            <div className="cu-loading">Loading…</div>
          ) : (
            <>
              {paginated.map((u) => {
                const org =
                  u?.freshbooksClientId?.organization ||
                  u.organization ||
                  u.companyName ||
                  "";
                const orders = orderCounts[u.id] ?? 0;
                const spent = fmtMoney(orderSums[u.id] || 0);
                const avatarUrl =
                  u.profileImageURL ||
                  u.profileImageUrl ||
                  u.photoURL ||
                  u.avatarUrl ||
                  null;
                const lastOrder = fmtDateDMY(u.lastOrderAt);
                const since = fmtDateDMY(u.createdAt);
                const hasEmailVerified =
                  u.emailVerified !== undefined && u.emailVerified !== null;
                const emailVerified = hasEmailVerified
                  ? u.emailVerified === true
                  : null;
                const needsVerification =
                  emailVerified === false ||
                  (!hasEmailVerified && Boolean(u.requireEmailReverify));
                return (
                  <div
                    key={u.id}
                    className="cu-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveUser(u)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveUser(u);
                      }
                    }}
                  >
                    <div
                      className="cu-cell cu-cell--check"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label={`select ${u.firstName || u.lastName || u.id}`}
                      />
                    </div>
                    <div className="cu-cell cu-cell--customer">
                      <div className="cu-avatar">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="" />
                        ) : (
                          initials(u)
                        )}
                      </div>

                      <div className="cu-name">
                        {needsVerification && (
                          <span
                            style={{
                              padding: "0 6px",
                              borderRadius: 12,
                              background: "rgba(255, 193, 7, 0.16)",
                              marginRight: "6px",
                              color: "#c77c00",
                              fontSize: 12,
                              fontWeight: 600,
                              lineHeight: "18px",
                              border: "1px solid rgba(255,193,7,0.4)",
                            }}
                            title="Email verification required"
                          >
                            !
                          </span>
                        )}
                        {org ||
                          `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
                          "Unnamed"}
                      </div>
                    </div>
                    <div className="cu-cell cu-cell--orders">{orders}</div>
                    <div className="cu-cell cu-cell--last">{lastOrder}</div>
                    <div className="cu-cell cu-cell--type">
                      <TypeBadge user={u} />
                    </div>
                    <div className="cu-cell cu-cell--spent">{spent}</div>
                    <div className="cu-cell cu-cell--since">{since}</div>
                  </div>
                );
              })}
              <div className="cu-pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={page === i + 1 ? "active" : ""}
                    onClick={() => setCurrentPage(i + 1)}
                    aria-label={`Go to page ${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
